import PERMISSIONS from '@/constants/permissions';
import { FileKind } from '@/typings/api/files';
import { useHasAccess } from '@/utils/hooks';
import { F, getIntlTranslation } from '@/utils/intl';
import { UploadOutlined } from '@ant-design/icons';
import { Button, Input, Segmented, Space, Upload } from 'antd';
import React from 'react';
import { useIntl } from 'umi';
import { acceptFor } from './fileDisplay';

import styles from './FileLibraryToolbar.less';

interface FileLibraryToolbarProps {
  search?: string;
  onSearch: (value: string) => void;
  /** Omitted when the surrounding slot only accepts one kind anyway. */
  kind?: FileKind;
  onKindChange?: (kind?: FileKind) => void;
  kinds?: FileKind[];
  uploading?: boolean;
  onUpload: (file: File) => void;
}

/** Search, kind filter and upload — the same controls wherever files are shown. */
const FileLibraryToolbar: React.FC<FileLibraryToolbarProps> = ({
  search,
  onSearch,
  kind,
  onKindChange,
  kinds,
  uploading,
  onUpload,
}) => {
  const intl = useIntl();
  // Email authors may upload without holding files_upload (FilestorePolicy),
  // so the button is hidden only from someone who can do neither.
  const canUpload = useHasAccess([PERMISSIONS.Files.UPLOAD]);
  const canAuthorEmails = useHasAccess([PERMISSIONS.EmailTemplate.DRAFT]);

  return (
    <div className={styles.toolbar}>
      <Space wrap>
        <Input.Search
          allowClear
          className={styles.search}
          defaultValue={search}
          onSearch={onSearch}
          onChange={(event) => {
            // Clearing with the ✕ should show everything again immediately;
            // waiting for a submit that never comes looks broken.
            if (event.target.value === '') onSearch('');
          }}
          placeholder={getIntlTranslation(intl, {
            id: 'components.fileLibrary.search',
            defaultMessage: 'Search files',
          })}
        />

        {onKindChange && (
          <Segmented
            value={kind ?? 'all'}
            onChange={(value) =>
              onKindChange(value === 'all' ? undefined : (value as FileKind))
            }
            options={[
              {
                value: 'all',
                label: (
                  <F
                    id="common.actions.all"
                    defaultMessage="All"
                  />
                ),
              },
              {
                value: 'image',
                label: (
                  <F
                    id="components.fileLibrary.kind.image"
                    defaultMessage="Images"
                  />
                ),
              },
              {
                value: 'font',
                label: (
                  <F
                    id="components.fileLibrary.kind.font"
                    defaultMessage="Fonts"
                  />
                ),
              },
              {
                value: 'document',
                label: (
                  <F
                    id="components.fileLibrary.kind.document"
                    defaultMessage="Documents"
                  />
                ),
              },
            ]}
          />
        )}
      </Space>

      {(canUpload || canAuthorEmails) && (
        <Upload
          accept={acceptFor(kinds)}
          showUploadList={false}
          // Handled here rather than by antd's own uploader: the request needs
          // the app's auth headers, which `request()` already attaches, and the
          // response has to land in the library's own state.
          beforeUpload={(file) => {
            onUpload(file as File);
            return false;
          }}
        >
          <Button type="primary" icon={<UploadOutlined />} loading={uploading}>
            <F id="common.actions.upload" defaultMessage="Upload" />
          </Button>
        </Upload>
      )}
    </div>
  );
};

export default FileLibraryToolbar;
